'use client'

import type { ReactNode } from 'react'
import { createContext, useContext, useMemo } from 'react'

import {
  createI18n,
  DEFAULT_FALLBACK_LANGUAGE,
  type OnMissingKeyCallback,
  type TranslationFunction,
  type TranslationsByLanguage,
} from '.'

export type I18NContext = {
  language: string
  messages: TranslationsByLanguage
  t: TranslationFunction
}

export const I18NContext = createContext<I18NContext>({
  language: 'en',
  messages: {},
  t: () => {
    return ''
  },
})

export const useI18N = () => useContext(I18NContext)

export type ClientI18NProviderProps = {
  children: ReactNode
  language: string
  messages: TranslationsByLanguage
  /** Called when a key resolves to itself, i.e. no translation was found for it. Optional — omit to keep the previous silent behavior. */
  onMissingKey?: OnMissingKeyCallback
}

export const ClientI18NProvider = ({
  children,
  language,
  messages,
  onMissingKey,
}: ClientI18NProviderProps): React.JSX.Element => {
  const t = useMemo(
    () =>
      createI18n(messages, language, DEFAULT_FALLBACK_LANGUAGE, onMissingKey),
    [messages, language, onMissingKey],
  )

  const value = useMemo(
    () => ({
      language,
      messages,
      t,
    }),
    [language, messages, t],
  )

  return <I18NContext.Provider value={value}>{children}</I18NContext.Provider>
}
